import type {
  BookAssemblyDraft,
  BookAssemblyRunState,
  BookExportCurrentStage,
  BookExportJob,
  BookExportSettings,
} from "@/features/book-assembly/book-assembly.types";

export const activeBookExportJobTimeoutMs = 10 * 60_000;

const runProgressByStatus: Record<BookAssemblyRunState["status"], number> = {
  idle: 0,
  validating: 10,
  assembling: 35,
  rendering: 70,
  saving: 90,
  failed: 100,
  succeeded: 100,
};

const runStatusByCurrentStage: Record<BookExportCurrentStage, BookAssemblyRunState["status"]> = {
  validating: "validating",
  building_manifest: "assembling",
  rendering_pdf: "rendering",
  rendering_docx: "rendering",
  uploading_artifacts: "saving",
  finalizing: "saving",
};

const runProgressByCurrentStage: Record<BookExportCurrentStage, number> = {
  validating: 12,
  building_manifest: 34,
  rendering_pdf: 62,
  rendering_docx: 74,
  uploading_artifacts: 88,
  finalizing: 96,
};

export function createBookAssemblyRunState(
  status: BookAssemblyRunState["status"],
  patch: Partial<BookAssemblyRunState> = {},
): BookAssemblyRunState {
  return {
    errorCode: null,
    errorMessage: null,
    progressPercent: runProgressByStatus[status],
    result: null,
    status,
    ...patch,
  };
}

export function normalizeBookExportSettings(
  settings: BookExportSettings,
): BookExportSettings {
  return {
    ...settings,
    authorName: settings.authorName.trim(),
    authorNote: settings.authorNote.trim(),
    bookTitle: settings.bookTitle.trim(),
    bookSubtitle: settings.bookSubtitle.trim(),
    dedication: settings.dedication.trim(),
    seriesName: settings.seriesName.trim(),
    specialThanks: settings.specialThanks.trim(),
    volumeLabel: settings.volumeLabel.trim(),
  };
}

export function mapBookExportJobToRunState(
  job: Pick<BookExportJob, "currentStage" | "failureCode" | "failureMessage" | "status"> | null | undefined,
): BookAssemblyRunState {
  if (!job) {
    return createBookAssemblyRunState("idle");
  }

  if (job.status === "failed") {
    return createBookAssemblyRunState("failed", {
      errorCode: job.failureCode,
      errorMessage: job.failureMessage,
    });
  }

  if (job.status === "complete") {
    return createBookAssemblyRunState("succeeded");
  }

  const status = job.currentStage
    ? runStatusByCurrentStage[job.currentStage]
    : "validating";

  return createBookAssemblyRunState(status, {
    progressPercent: job.currentStage
      ? runProgressByCurrentStage[job.currentStage]
      : runProgressByStatus.validating,
  });
}

export function isBookExportJobActive<T extends Pick<BookExportJob, "status">>(
  job: T | null | undefined,
): job is T {
  return job?.status === "pending" || job?.status === "processing";
}

export function isBookExportJobStale(
  job: Pick<BookExportJob, "status" | "updatedAt"> | null | undefined,
  now = Date.now(),
  timeoutMs = activeBookExportJobTimeoutMs,
) {
  if (!isBookExportJobActive(job)) {
    return false;
  }

  const updatedAt = Date.parse(job.updatedAt);
  return Number.isNaN(updatedAt) ? false : now - updatedAt > timeoutMs;
}

export function markBookExportJobAsStaleFailure(
  job: BookExportJob,
  completedAt = new Date().toISOString(),
): BookExportJob {
  return {
    ...job,
    completedAt,
    currentStage: "finalizing",
    failureCode: "BOOK_EXPORT_STALE",
    failureMessage: "BOOK_EXPORT_PROCESSING_FAILED",
    status: "failed",
    updatedAt: completedAt,
  };
}

export function getBookExportCurrentStageLabelKey(
  stage: BookExportCurrentStage | null | undefined,
) {
  switch (stage) {
    case "building_manifest":
      return "bookAssembly.generate.stage.buildingManifest";
    case "rendering_docx":
      return "bookAssembly.generate.stage.renderingDocx";
    case "rendering_pdf":
      return "bookAssembly.generate.stage.renderingPdf";
    case "uploading_artifacts":
      return "bookAssembly.generate.stage.uploadingArtifacts";
    case "finalizing":
      return "bookAssembly.generate.stage.finalizing";
    case "validating":
    default:
      return "bookAssembly.generate.stage.validating";
  }
}

export function canGenerateBookAssembly(draft: BookAssemblyDraft | null) {
  if (!draft) {
    return false;
  }

  return (
    draft.orderedItems.length > 0
    && Boolean(draft.exportConfig?.preset)
  );
}

export function getRunStatusLabelKey(status: BookAssemblyRunState["status"]) {
  switch (status) {
    case "validating":
      return "bookAssembly.generate.status.validating";
    case "assembling":
      return "bookAssembly.generate.status.assembling";
    case "rendering":
      return "bookAssembly.generate.status.rendering";
    case "saving":
      return "bookAssembly.generate.status.saving";
    case "failed":
      return "bookAssembly.generate.status.failed";
    case "succeeded":
      return "bookAssembly.generate.status.succeeded";
    default:
      return "bookAssembly.generate.status.idle";
  }
}
